/**
 * Scale gizmo that does not depend on MapLibre style layers.
 * Draws an 8-handle box from ToolCard-loaded geometries over Giro3D.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  matchScaleFeature,
  pickFeatureAtLonLat,
  scaleFeaturesFrom,
  type ScaleSourceFeature,
} from './scaleFeatureSource';
import {
  bboxFromGeometry,
  bboxRing,
  scaleGeometry,
  scaleHandlesFromBbox,
  type ScaleHandleId,
} from './vectorScaleBox';
import { useViewerStore } from '@/state/useViewerStore';
import {
  createScaleProjector,
  resolveMapRoot,
  resolveScaleZoom,
} from './scaleClientProjection';
import { scaleGeometrySvgPath } from './scaleGeometrySvgPath';
import {
  getVectorScaleEdit,
  useVectorScaleSessionVersion,
} from './vectorScaleSession';

export interface ScaleBoxOverlayProps {
  readonly layerId: string;
  readonly features: ReadonlyArray<ScaleSourceFeature>;
  readonly selectedFeatureId?: string | number | null;
  readonly container?: HTMLElement | null;
  readonly armed: boolean;
  readonly onCommit: (feature: ScaleSourceFeature) => void;
}

export function ScaleBoxOverlay({
  layerId,
  features,
  selectedFeatureId = null,
  container = null,
  armed,
  onCommit,
}: ScaleBoxOverlayProps) {
  const scaleSessionVersion = useVectorScaleSessionVersion();
  const [active, setActive] = useState<ScaleSourceFeature | null>(null);
  const [frame, setFrame] = useState(0);
  const dragRef = useRef<ScaleHandleId | null>(null);
  const camera = useViewerStore((state) => state.camera);
  const mapRoot = resolveMapRoot(container);

  const normalized = useMemo(() => scaleFeaturesFrom(features), [features]);
  const projector = useMemo(
    () => createScaleProjector(mapRoot, camera),
    [mapRoot, camera, frame],
  );

  useEffect(() => {
    if (armed && mapRoot) {
      mapRoot.style.cursor = 'grab';
      mapRoot.querySelectorAll('canvas').forEach((node) => {
        node.style.cursor = 'grab';
      });
    }
    return () => {
      if (mapRoot) {
        mapRoot.style.cursor = '';
        mapRoot.querySelectorAll('canvas').forEach((node) => {
          node.style.cursor = '';
        });
      }
    };
  }, [armed, mapRoot]);

  useEffect(() => {
    if (!armed) {
      setActive(null);
      return;
    }
    const selected = matchScaleFeature(normalized, selectedFeatureId);
    const committed = getVectorScaleEdit(layerId, selectedFeatureId);
    if (committed) setActive(committed.feature);
    else if (selected) setActive(selected);
  }, [armed, layerId, normalized, scaleSessionVersion, selectedFeatureId]);

  useEffect(() => {
    if (!armed) return;
    const rect = mapRoot?.getBoundingClientRect();
    console.log('[scale] overlay arm', {
      layerId,
      featureCount: normalized.length,
      selectedFeatureId,
      selectedMatched: Boolean(matchScaleFeature(normalized, selectedFeatureId)?.geometry),
      cameraLonLat: camera?.lonLat,
      zoom: resolveScaleZoom(camera, rect?.height ?? 0),
      mapSize: rect ? { w: Math.round(rect.width), h: Math.round(rect.height) } : null,
    });
  }, [armed, camera, layerId, mapRoot, normalized, selectedFeatureId]);

  useEffect(() => {
    if (!armed || !active) return;
    let raf = 0;
    const tick = () => {
      setFrame((value) => value + 1);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, armed]);

  useEffect(() => {
    if (!armed) return;
    const onDown = (event: PointerEvent) => {
      if (event.button !== 0) return;
      if (mapRoot && event.target instanceof Node && !mapRoot.contains(event.target)) {
        return;
      }
      const point = projector.clientToLonLat(event.clientX, event.clientY);
      console.log('[scale] overlay click', {
        layerId,
        lng: point?.[0],
        lat: point?.[1],
        featureCount: normalized.length,
        selectedFeatureId,
      });
      if (!point) return;
      if (dragRef.current) return;
      const hitHandle = hitHandleAt(active, projector, event.clientX, event.clientY);
      if (hitHandle && active) {
        dragRef.current = hitHandle;
        console.log('[scale] overlay drag handle', hitHandle);
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const picked = pickFeatureAtLonLat(normalized, point);
      console.log('[scale] overlay picked', {
        id: picked?.id,
        geometry: (picked?.geometry as { type?: string } | undefined)?.type,
        bbox: picked ? bboxFromGeometry(picked.geometry) : null,
      });
      if (!picked) return;
      setActive(picked);
      event.preventDefault();
      event.stopPropagation();
    };
    const onMove = (event: PointerEvent) => {
      if (!dragRef.current || !active) return;
      const point = projector.clientToLonLat(event.clientX, event.clientY);
      if (!point) return;
      const nextGeometry = scaleGeometry(active.geometry, dragRef.current, point);
      setActive({ ...active, geometry: nextGeometry });
    };
    const onUp = () => {
      if (!dragRef.current) return;
      console.log('[scale] overlay commit', {
        layerId,
        id: active?.id,
        bbox: active ? bboxFromGeometry(active.geometry) : null,
      });
      dragRef.current = null;
      if (active) onCommit(active);
    };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
    };
  }, [active, armed, layerId, mapRoot, normalized, onCommit, projector, selectedFeatureId]);

  if (typeof document === 'undefined' || !armed || !active) return null;
  const bbox = bboxFromGeometry(active.geometry);
  if (!bbox) return null;
  const ring = bboxRing(bbox)
    .map(([lon, lat]) => projector.lonLatToClient(lon, lat))
    .filter((point): point is { x: number; y: number } => Boolean(point));
  const handles = scaleHandlesFromBbox(bbox)
    .map((handle) => {
      const point = projector.lonLatToClient(handle.lonLat[0], handle.lonLat[1]);
      return point ? { id: handle.id, ...point } : null;
    })
    .filter((handle): handle is { id: ScaleHandleId; x: number; y: number } => Boolean(handle));
  if (ring.length < 2 || handles.length === 0) return null;

  const path = ring.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
  const geometryPath = scaleGeometrySvgPath(active.geometry, projector);
  const mapRect = mapRoot?.getBoundingClientRect();
  if (!mapRect) return null;

  return createPortal(
    <svg
      data-testid="scale-box-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 40,
      }}
    >
      <defs>
        <clipPath id="scale-box-map-clip">
          <rect
            x={mapRect.left}
            y={mapRect.top}
            width={mapRect.width}
            height={mapRect.height}
          />
        </clipPath>
      </defs>
      <g clipPath="url(#scale-box-map-clip)">
        {geometryPath ? (
          <path
            data-testid="scale-feature-shape"
            d={geometryPath}
            fill="#ff8a80"
            fillOpacity="0.28"
            fillRule="evenodd"
            stroke="#ff7068"
            strokeWidth="4"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
        <path d={path} fill="none" stroke="#111111" strokeWidth="1.5" />
        {handles.map((handle) => (
          <circle
            key={handle.id}
            cx={handle.x}
            cy={handle.y}
            r={6}
            fill="#ffffff"
            stroke="#111111"
            strokeWidth="1"
          />
        ))}
      </g>
    </svg>,
    document.body,
  );
}

function hitHandleAt(
  feature: ScaleSourceFeature | null,
  projector: ReturnType<typeof createScaleProjector>,
  clientX: number,
  clientY: number,
): ScaleHandleId | null {
  if (!feature) return null;
  const bbox = bboxFromGeometry(feature.geometry);
  if (!bbox) return null;
  let nearest: { id: ScaleHandleId; dist: number } | null = null;
  for (const handle of scaleHandlesFromBbox(bbox)) {
    const point = projector.lonLatToClient(handle.lonLat[0], handle.lonLat[1]);
    if (!point) continue;
    const dist = Math.hypot(point.x - clientX, point.y - clientY);
    if (dist <= 16 && (!nearest || dist < nearest.dist)) {
      nearest = { id: handle.id, dist };
    }
  }
  return nearest?.id ?? null;
}
